import { useCallback, useEffect, useRef, useState } from "react";
import {
  getDocumentColumnSettings,
  saveDocumentColumnSettings,
  type DocumentColumnSettings,
  type DocumentColumnView,
} from "@/lib/dokumente";
import {
  getDefaultDocumentColumns,
  normalizeDocumentColumnSelection,
  type DocumentColumnKey,
} from "@/pages/dokumente/documentColumns";

export type { DocumentColumnSettings, DocumentColumnView };

export function useDocumentTableSettings() {
  const [settings, setSettings] = useState<DocumentColumnSettings>({
    desktop: null,
    mobile: null,
  });
  const [loaded, setLoaded] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    void getDocumentColumnSettings()
      .then((next) => {
        if (!isMountedRef.current) return;
        setSettings(next);
      })
      .catch(() => {
        if (!isMountedRef.current) return;
        setSettings({ desktop: null, mobile: null });
      })
      .finally(() => {
        if (isMountedRef.current) setLoaded(true);
      });
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const saveSettings = useCallback(async (next: DocumentColumnSettings) => {
    setSettings(next);
    try {
      const saved = await saveDocumentColumnSettings(next);
      if (isMountedRef.current) setSettings(saved);
      return saved;
    } catch (err) {
      console.warn("[Dokumente] saveColumnSettings failed:", err);
      return next;
    }
  }, []);

  return { settings, setSettings, saveSettings, loaded };
}

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia(query).matches : false,
  );

  useEffect(() => {
    const mq = window.matchMedia(query);
    const handler = () => setMatches(mq.matches);
    handler();
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [query]);

  return matches;
}

export function getVisibleDocumentColumns(
  settings: DocumentColumnSettings,
  isMobile: boolean,
): DocumentColumnKey[] {
  const view = isMobile ? settings.mobile : settings.desktop;
  if (view?.columns?.length) {
    return normalizeDocumentColumnSelection(view.columns);
  }
  return getDefaultDocumentColumns();
}

export function getVisibleDocumentWidths(
  settings: DocumentColumnSettings,
  isMobile: boolean,
): Record<string, number> {
  const view = isMobile ? settings.mobile : settings.desktop;
  return view?.widths ?? {};
}
